import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid, Paper, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AdvertisingLayout from '../../layouts/AdvertisingLayout';

// Import Animation icon
import animationIcon from '../../assets/Sales/Stand Out More.png';

// Import all Animated images
import animated1 from '../../assets/Sales/Animated/AthleticHub.gif';
import animated2 from '../../assets/Sales/Animated/DanceStreet.gif';
import animated3 from '../../assets/Sales/Animated/Kids-Entertainment-Ad-Example.gif';
import animated4 from '../../assets/Sales/Animated/NoLimits.gif';
import animated5 from '../../assets/Sales/Animated/ThePit.gif';

const animatedImages = [animated1, animated2, animated3, animated4, animated5];

function Animation() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [autoRotateKey, setAutoRotateKey] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotation effect
  useEffect(() => {
    if (isHovering) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        return (prevIndex + 1) % animatedImages.length;
      });
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [isHovering, autoRotateKey]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => {
      return prevIndex === 0 ? animatedImages.length - 1 : prevIndex - 1;
    });
    setAutoRotateKey(prev => prev + 1);
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      return (prevIndex + 1) % animatedImages.length;
    });
    setAutoRotateKey(prev => prev + 1);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setAutoRotateKey(prev => prev + 1);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setAutoRotateKey(prev => prev + 1);
  };

  // Get current 3 images to display
  const getCurrentImages = () => {
    const images = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % animatedImages.length;
      images.push(animatedImages[index]);
    }
    return images;
  };

  return (
    <AdvertisingLayout>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #247AA8 0%, #1E6A92 100%)',
          color: 'white',
          py: 6,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' },
              mb: 2
            }}
          >
            Animate your Premium Ad
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 300,
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}
          >
            Stand Out More
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: 6, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Left Column - Text Content with Icon */}
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 0, backgroundColor: 'white', overflow: 'hidden' }}>
                {/* Top Section - Text Content with Icon */}
                <Box sx={{ p: 3, display: 'flex', gap: 3 }}>
                  {/* Text Content */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: '#333132' }}>
                      Stand out more by adding animation and movement to your Premium or POW Ad. Upgrade to a Premium Plus 
                      or POW+ (gif) format to grab that added attention.
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, fontWeight: 600, color: '#000000' }}>
                      What's included with your Animated Ad?
                    </Typography>

                    <Box component="ul" sx={{ pl: 2, mb: 2, listStyleType: 'disc' }}>
                      <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                        Free animation design for a new ad or to your current premium ad
                      </Typography>
                      <Typography component="li" sx={{ mb: 1, color: '#333132', display: 'list-item' }}>
                        Available to be added to school term or 6, or 12 month premium campaigns
                      </Typography>
                      <Typography component="li" sx={{ mb: 0, color: '#333132', display: 'list-item' }}>
                        Professional recommendations on how best to add movement and animation to your ad
                      </Typography>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 0, lineHeight: 1.7, color: '#333132' }}>
                      Make your ad animated today for as little as $100 per school term ($400 per year). Speak to our 
                      professional team of Consultants to discuss your options and get a quote.
                    </Typography>
                  </Box>

                  {/* Animation Icon - Right side of text */}
                  <Box sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
                    <Box
                      component="img"
                      src={animationIcon}
                      alt="Animation Upgrade"
                      sx={{
                        maxWidth: '220px',
                        width: '100%',
                        height: 'auto'
                      }}
                    />
                  </Box>
                </Box>

                {/* Bottom Section - Terms & Conditions with Grey Background */}
                <Box sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="body1" sx={{ mb: 1, lineHeight: 1.7, fontWeight: 600, color: '#000000' }}>
                    Terms & Conditions:
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 0, lineHeight: 1.6, color: '#666', fontSize: '0.875rem' }}>
                    Animated ads are $100 per term or $400 per year on top of each selected school advertising charge. 
                    Animations can only be applied to POW and Premium ads. Animation upgrades can be provided for your 
                    current ad and will be charged at $100 per term or $400 per year for your remaining campaign period. 
                    Available for current and new advertising businesses. Not available on paper newsletters. No further 
                    discounts or package prices apply. Mid-campaign artwork changes are available (subject to Austnews 
                    Advertising artwork change pricing structure); mid-campaign animation artwork changes are available 
                    and will be charged at $100 per term or $400 per year per ad per school.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Pricing Card */}
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#4A9FCC',
                  color: 'white',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                {/* Premium Plus Banner */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -10,
                    left: -10,
                    backgroundColor: '#FF8C00',
                    color: 'white',
                    px: 2,
                    py: 0.5,
                    transform: 'rotate(-3deg)',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    boxShadow: 3,
                    letterSpacing: 0.5
                  }}
                >
                  PREMIUM PLUS
                </Box>

                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    textAlign: 'center',
                    mb: 1.5,
                    mt: 0.5,
                    fontSize: '1.5rem'
                  }}
                >
                  Animation UPGRADE
                </Typography>
                
                <Box sx={{ backgroundColor: 'white', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
                  {/* Grey top section - Price */}
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2.5 }}>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#4A9FCC',
                        textAlign: 'center',
                        fontSize: '2.5rem',
                        mb: 0.5
                      }}
                    >
                      $400
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666',
                        textAlign: 'center'
                      }}
                    >
                      12 months animation upgrade
                    </Typography>
                  </Box>

                  {/* White bottom section - Features */}
                  <Box sx={{ p: 2.5, pt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        Available on Premium Ads only
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        Minimum 1 School Term Campaign
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          border: '2px solid #4A9FCC',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5,
                          flexShrink: 0
                        }}
                      >
                        <Typography sx={{ color: '#4A9FCC', fontSize: '0.875rem', fontWeight: 600 }}>›</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#4A9FCC', fontWeight: 600, fontSize: '0.875rem' }}>
                        Free Graphic Design
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    navigate('/advertising');
                    setTimeout(() => {
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }}
                  sx={{
                    backgroundColor: 'white',
                    color: '#4A9FCC',
                    fontWeight: 700,
                    py: 1,
                    fontSize: '1rem',
                    border: '2px solid white',
                    borderRadius: 1,
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#4A9FCC',
                      color: 'white',
                      borderColor: '#4A9FCC',
                      boxShadow: 'none',
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  Book Now
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Animated Sample Ads Section */}
      <Box sx={{ py: 8, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth="lg">
          <Paper elevation={0} sx={{ p: 4, backgroundColor: 'white', borderRadius: 2 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              sx={{ mb: 4, fontWeight: 600, color: '#333132', textAlign: 'left' }}
            >
              Animated Sample Ads
            </Typography>

            {/* Image Carousel */}
            <Box 
              sx={{ position: 'relative', mb: 4 }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
            <Grid container spacing={3} justifyContent="center">
              {getCurrentImages().map((image, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Paper
                    elevation={3}
                    sx={{
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 6
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={image}
                      alt={`Animated Ad ${idx + 1}`}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        display: 'block'
                      }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Navigation Arrows */}
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: { xs: -10, md: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: { xs: -10, md: -20 },
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          {/* Dot Indicators */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
            {animatedImages.map((_, index) => (
              <Box
                key={index}
                onClick={() => handleDotClick(index)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: currentIndex === index ? '#247AA8' : '#ccc',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease',
                  '&:hover': {
                    backgroundColor: currentIndex === index ? '#247AA8' : '#999'
                  }
                }}
              />
            ))}
          </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            Copyright Australian Newsletter Services Pty Ltd 2016-2025 | <a href="/advertising/terms-and-conditions" style={{ color: '#247AA8', textDecoration: 'none' }}>Advertising Terms & Conditions</a> | All Rights Reserved
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            QAssure Number: 11954 | Accredited GITC Supplier: Q-6019 | ABN 24 010 367 478
          </Typography>
        </Container>
      </Box>
    </AdvertisingLayout>
  );
}

export default Animation;

